import { DeleteDialog } from "@/components/dialogs/DeleteDialog";
import { ExpertDisplayName } from "@/components/display/ExpertDisplayName";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchInput } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsDummyButton } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EXPERT_TYPE_LABELS } from "@/consts/experts";
import { cn } from "@/lib/utils";
import { Expert } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faXmark,
  faEllipsisVertical,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useEffect, useState } from "react";
import { useAssistContext } from "../../Assist/AssistContext";
import { ExpertAvatar } from "../ExpertAvatar";
import { useExpertSelection } from "./ExpertSelectionContext";

const MAX_VISIBLE_AVATARS = 3;
const MAX_NAME_LENGTH = 32;

type ExpertTypeFilter = "all" | Expert["expertType"];

const EXPERT_TYPE_FILTERS: { value: ExpertTypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  ...(
    Object.entries(EXPERT_TYPE_LABELS).filter(
      ([type]) => type !== "interviewer",
    ) as [Expert["expertType"], string][]
  ).map(([value, label]) => ({
    value,
    label,
  })),
];

const truncateName = (name: string) =>
  name.length > MAX_NAME_LENGTH ? `${name.slice(0, MAX_NAME_LENGTH)}...` : name;

type ProjectExpertsIndicatorProps = {
  className?: string;
};

export const ProjectExpertsIndicator: React.FC<
  ProjectExpertsIndicatorProps
> = ({ className }) => {
  const { kitExperts, unlinkExperts, unselectExpert, selectExperts } =
    useExpertSelection();
  const { setActiveChatUuid, setForwardChatUuid } = useAssistContext();
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [expertTypeFilter, setExpertTypeFilter] =
    useState<ExpertTypeFilter>("all");
  const [expertToRemove, setExpertToRemove] = useState<Expert | null>(null);

  useEffect(() => {
    if (!open) {
      setSearchText("");
      setExpertTypeFilter("all");
    }
  }, [open]);

  const visibleExperts = kitExperts.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = kitExperts.length - visibleExperts.length;

  const filteredKitExperts = kitExperts.filter(
    (expert) =>
      expert.name.toLowerCase().includes(searchText.trim().toLowerCase()) &&
      (expertTypeFilter === "all" || expert.expertType === expertTypeFilter),
  );

  const handleRemoveExpert = (expertUuid: string) => {
    unselectExpert(expertUuid);
    unlinkExperts(new Set([expertUuid]));
    setOpen(false);
  };

  const handleStartChat = (expertUuid: string) => {
    selectExperts(new Set([expertUuid]));
    setActiveChatUuid(null);
    setForwardChatUuid(null);
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Manage workbook experts"
            title="Manage workbook experts"
            className={cn(
              "flex cursor-pointer flex-row-reverse items-center",
              className,
            )}
          >
            {overflowCount > 0 && (
              <div className="bg-foreground-2 text-foreground border-background -m-1 box-content flex h-8 w-8 items-center justify-center rounded-full border-1 text-xs font-semibold">
                +{overflowCount}
              </div>
            )}
            {[...visibleExperts].reverse().map((expert) => (
              <ExpertAvatar
                key={expert.uuid}
                expert={expert}
                size="md"
                className="border-background -m-1 box-content border-1"
              />
            ))}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="flex w-110 flex-col gap-2 p-2">
          <div className="text-md flex flex-row items-center gap-4 p-2 font-semibold">
            Workbook Experts
          </div>
          {kitExperts.length === 0 ? (
            <div className="text-foreground-5 px-2 pb-2 text-sm italic">
              No experts in this workbook yet
            </div>
          ) : (
            <>
              <Tabs value={expertTypeFilter} className="gap-0 px-2">
                <div className="flex flex-row gap-1 overflow-x-auto">
                  {EXPERT_TYPE_FILTERS.map(({ value, label }) => (
                    <TabsDummyButton
                      key={value}
                      isActive={expertTypeFilter === value}
                      onMouseUp={() => setExpertTypeFilter(value)}
                      className="h-7 px-3 text-xs"
                    >
                      {label}
                    </TabsDummyButton>
                  ))}
                </div>
              </Tabs>
              <div className="px-2">
                <SearchInput
                  placeholder="Search Experts..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="w-full"
                  autoFocus
                />
              </div>
              {filteredKitExperts.length === 0 ? (
                <div className="text-foreground-5 px-2 pb-2 text-sm italic">
                  No experts match your search
                </div>
              ) : (
                <div className="flex max-h-80 flex-col gap-1 overflow-y-auto">
                  {filteredKitExperts.map((expert) => (
                    <DropdownMenu key={expert.uuid}>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="hover:bg-background-5 flex w-full cursor-pointer flex-row items-center gap-2 rounded-md p-2 text-left"
                        >
                          {expert.name.length > MAX_NAME_LENGTH ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="min-w-0 flex-1">
                                  <ExpertDisplayName
                                    expert={{
                                      ...expert,
                                      name: truncateName(expert.name),
                                    }}
                                    className="truncate"
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{expert.name}</TooltipContent>
                            </Tooltip>
                          ) : (
                            <ExpertDisplayName
                              expert={expert}
                              className="min-w-0 flex-1 truncate"
                            />
                          )}
                          <div
                            className={
                              "text-foreground-8 line-clamp-3 text-center text-xs"
                            }
                          >
                            {expert.expertType}
                          </div>
                          <div
                            className={
                              "flex h-6 w-6 items-center justify-center rounded-full"
                            }
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                          </div>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem
                          onClick={() => handleStartChat(expert.uuid)}
                        >
                          <FontAwesomeIcon icon={faComment} className="mr-2" />
                          Start a chat
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setExpertToRemove(expert)}
                          className="text-destructive"
                        >
                          <FontAwesomeIcon icon={faXmark} className="mr-2" />
                          Remove from this workbook
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ))}
                </div>
              )}
            </>
          )}
        </PopoverContent>
      </Popover>
      <DeleteDialog
        open={!!expertToRemove}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setExpertToRemove(null);
          }
        }}
        name={expertToRemove ? `${expertToRemove.name} from this workbook` : ""}
        onDeleteClick={() => {
          if (expertToRemove) {
            handleRemoveExpert(expertToRemove.uuid);
          }
        }}
      />
    </>
  );
};
